import React from 'react';
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { useAccountHolderDetailsPageLogic } from '../hooks/useAccountHolderDetailsPageLogic';
import {
  formatDateTime,
  formatPhoneNumber,
  formatSubscriptionStatusLabel,
  getNameInitials,
  getSubscriptionStatusClass,
} from '../mappers/accountHoldersFormatters';
import '../styles/AccountHolderDetails.css';
import { adminColors, adminTypography } from '../../../styles/adminDesignTokens';
import AdminSearchField from '../../../components/admin/AdminSearchField';
import AdminSurface from '../../../components/admin/AdminSurface';
import { adminCombineSx, adminFormControlSx, adminFormFieldSx, adminLinkButtonSx } from '../../../styles/adminComponentStyles';

const AccountHolderDetailsPage: React.FC = () => {
  const { loading, error, ownerDetails, stores, handleStoreClick } = useAccountHolderDetailsPageLogic();
  const [storeSearch, setStoreSearch] = React.useState('');
  const [storeStatus, setStoreStatus] = React.useState('all');

  if (loading) {
    return (
      <Box className="AccountHolderDetailsPage">
        <Box className="ahd-loading-wrap">
          <CircularProgress size={28} />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box className="AccountHolderDetailsPage">
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!ownerDetails) {
    return (
      <Box className="AccountHolderDetailsPage">
        <Typography className="ahd-not-found">User not found.</Typography>
      </Box>
    );
  }

  const storeStatuses = Array.from(
    new Set(stores.map((store) => (store.subscription_status || '').toLowerCase()).filter(Boolean)),
  ).sort();
  const normalizedStoreSearch = storeSearch.trim().toLowerCase();
  const filteredStores = stores.filter((store) => {
    const status = (store.subscription_status || '').toLowerCase();
    const searchableText = [store.store_name, store.address, status].filter(Boolean).join(' ').toLowerCase();
    return (storeStatus === 'all' || status === storeStatus)
      && (!normalizedStoreSearch || searchableText.includes(normalizedStoreSearch));
  });

  return (
    <Box className="AccountHolderDetailsPage">
      <Box sx={{ pb: 1.25 }}>
        <Breadcrumbs>
          <Button component={Link} to="/dashboard/account-holders" variant="text" sx={adminLinkButtonSx}>
            Users
          </Button>
          <Typography sx={{ ...adminTypography.bodyStrong, color: adminColors.text }}>
            Details
          </Typography>
        </Breadcrumbs>
      </Box>

      <Box className="ahd-profile-card">
        <Box className="ahd-profile-main">
          <Box className="ahd-avatar">{getNameInitials(ownerDetails.name)}</Box>
          <Box>
            <Typography className="ahd-name">{ownerDetails.name || '-'}</Typography>
            <Typography className="ahd-email">{ownerDetails.email_address || '-'}</Typography>
            <Typography className="ahd-phone">{formatPhoneNumber(ownerDetails.phone_number, true)}</Typography>
          </Box>
        </Box>

        <Box className="ahd-registration">
          <Typography className="ahd-registration-label">Registration Date</Typography>
          <Typography className="ahd-registration-value">{formatDateTime(ownerDetails.registration_date)}</Typography>
        </Box>
      </Box>

      <Typography className="ahd-section-title">
        Store List <span className="ahd-section-count">({String(stores.length).padStart(2, '0')})</span>
      </Typography>

      <AdminSurface
        sx={{
          backgroundColor: '#FFFFFF',
          border: '1px solid #e0e0e0',
          boxShadow: '0 2px 8px rgb(34 43 69 / 8%)',
          overflow: 'hidden',
          p: 0,
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'stretch', sm: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 1.5, px: 2, pt: 1.25 }}
        >
          <AdminSearchField
            placeholder="Search stores"
            value={storeSearch}
            onChange={(event) => setStoreSearch(event.target.value)}
            sx={adminCombineSx(adminFormFieldSx, { width: { xs: '100%', sm: 240 } })}
            inputProps={{ 'aria-label': 'Search stores' }}
          />
          <FormControl size="small" sx={adminCombineSx(adminFormControlSx, { minWidth: { xs: '100%', sm: 160 } })}>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={storeStatus}
              onChange={(event) => setStoreStatus(event.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              {storeStatuses.map((status) => (
                <MenuItem key={status} value={status}>
                  {formatSubscriptionStatusLabel(status)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <TableContainer className="ahd-table-container">
        <Table size="small" className="ahd-table">
          <TableHead>
            <TableRow className="ahd-head-row">
              <TableCell className="ahd-head-cell ahd-store-col">Store Name</TableCell>
              <TableCell className="ahd-head-cell">Address</TableCell>
              <TableCell className="ahd-head-cell ahd-status-col">Status</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {filteredStores.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="ahd-empty-cell">
                  <Typography variant="body2">No stores found for this user.</Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredStores.map((store, index) => (
                <TableRow
                  key={store.store_id}
                  className={index % 2 === 0 ? 'ahd-row' : 'ahd-row-alt'}
                  onClick={() => handleStoreClick(store.store_id)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleStoreClick(store.store_id);
                    }
                  }}
                >
                  <TableCell className="ahd-cell-text ahd-store-col">{store.store_name || '-'}</TableCell>
                  <TableCell className="ahd-cell-text">{store.address || '-'}</TableCell>
                  <TableCell className="ahd-status-col">
                    <span className={`ahd-status-badge ahd-status-${getSubscriptionStatusClass(store.subscription_status)}`}>
                      {formatSubscriptionStatusLabel(store.subscription_status)}
                    </span>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        </TableContainer>
      </AdminSurface>
    </Box>
  );
};

export default AccountHolderDetailsPage;
